import {
  ROLE_FRONT_DESK,
  ROLE_JDTECH_ADMIN,
  ROLE_SALON_ADMIN,
  ROLE_SYSTEM_ADMIN,
} from "./constants";

/**
 * Organization selection for the JDTech super admin.
 *
 * A super admin picks one organization and then works inside it as that
 * organization's admin. The choice lives in sessionStorage (per browser tab)
 * and is sent to the API in the x-org-id header.
 */

export const ORG_STORAGE_KEYS = {
  id: "selectedOrgId",
  name: "selectedOrgName",
  defaultLocationId: "selectedOrgDefaultLocationId",
  defaultLocationName: "selectedOrgDefaultLocationName",
};

/** Roles a super admin gains while an organization is selected. */
export const ACTING_ROLE_IDS = [
  ROLE_SYSTEM_ADMIN,
  ROLE_SALON_ADMIN,
  ROLE_FRONT_DESK,
];

export const ORG_SELECTION_REQUIRED_MESSAGE = "Organization selection required";
export const ORG_NOT_FOUND_MESSAGE = "Organization not found";
export const ORG_OVERRIDE_NOT_ALLOWED_MESSAGE =
  "Organization override not allowed";
export const LOCATION_NOT_AUTHORIZED_MESSAGE = "Location not authorized";

const BOOTSTRAP_PATH = "users/userAuthorization";

function toPositiveInt(value) {
  if (value === null || value === undefined) return null;
  const text = String(value).trim();
  if (!/^\d+$/.test(text)) return null;
  const parsed = Number(text);
  return parsed > 0 ? parsed : null;
}

export function isSuperAdmin(user) {
  return (
    Array.isArray(user?.userRoles) && user.userRoles.includes(ROLE_JDTECH_ADMIN)
  );
}

/** The stored selection, or null when none is stored or it is unusable. */
export function readSelectedOrg() {
  try {
    const orgId = toPositiveInt(sessionStorage.getItem(ORG_STORAGE_KEYS.id));
    if (orgId === null) return null;

    return {
      org_id: orgId,
      org_name: sessionStorage.getItem(ORG_STORAGE_KEYS.name) || "",
      default_location_id: toPositiveInt(
        sessionStorage.getItem(ORG_STORAGE_KEYS.defaultLocationId)
      ),
      default_location_name:
        sessionStorage.getItem(ORG_STORAGE_KEYS.defaultLocationName) || "",
    };
  } catch {
    return null;
  }
}

export function persistSelectedOrg(org) {
  const orgId = toPositiveInt(org?.org_id);
  if (orgId === null) return;

  try {
    sessionStorage.setItem(ORG_STORAGE_KEYS.id, String(orgId));
    sessionStorage.setItem(ORG_STORAGE_KEYS.name, org.org_name || "");

    const locationId = toPositiveInt(org.default_location_id);
    if (locationId === null) {
      sessionStorage.removeItem(ORG_STORAGE_KEYS.defaultLocationId);
      sessionStorage.removeItem(ORG_STORAGE_KEYS.defaultLocationName);
    } else {
      sessionStorage.setItem(
        ORG_STORAGE_KEYS.defaultLocationId,
        String(locationId)
      );
      sessionStorage.setItem(
        ORG_STORAGE_KEYS.defaultLocationName,
        org.default_location_name || ""
      );
    }
  } catch {
    /* sessionStorage unavailable */
  }
}

export function clearSelectedOrg() {
  try {
    Object.values(ORG_STORAGE_KEYS).forEach((key) =>
      sessionStorage.removeItem(key)
    );
  } catch {
    /* sessionStorage unavailable */
  }
}

/** True when the app must show the organization picker instead of its pages. */
export function needsOrgSelection(user, selectedOrg) {
  return isSuperAdmin(user) && !selectedOrg;
}

/**
 * The user the rest of the app sees. For a super admin with an organization
 * selected this is the signed-in user placed inside that organization: its
 * org id, its default location, and the admin and front desk roles, so every
 * page and menu behaves as it does for that organization's own admin.
 * Everyone else is returned unchanged.
 */
export function toEffectiveUser(user, selectedOrg) {
  if (!user || !selectedOrg || !isSuperAdmin(user)) return user;

  return {
    ...user,
    homeOrg: user.org,
    org: String(selectedOrg.org_id),
    userRoles: Array.from(new Set([...user.userRoles, ...ACTING_ROLE_IDS])),
    location_id: selectedOrg.default_location_id ?? null,
    location_name: selectedOrg.default_location_name || "",
    assignedLocations: [],
  };
}

/** Value for the x-org-id header on a request to `url`, or null to send none. */
export function getOrgHeaderValue(url) {
  if (typeof url === "string" && url.includes(BOOTSTRAP_PATH)) return null;
  const selected = readSelectedOrg();
  return selected ? String(selected.org_id) : null;
}

/** True when the API says the stored selection is missing or no longer valid. */
export function isStaleOrgError(status, message) {
  return (
    (status === 403 && message === ORG_SELECTION_REQUIRED_MESSAGE) ||
    // The signed-in user is not a super admin (any more): the selection was
    // left behind by another user of this tab
    (status === 403 && message === ORG_OVERRIDE_NOT_ALLOWED_MESSAGE) ||
    (status === 400 && message === ORG_NOT_FOUND_MESSAGE)
  );
}

/** True when the API rejects the location sent with the request. */
export function isStaleLocationError(status, message) {
  return status === 403 && message === LOCATION_NOT_AUTHORIZED_MESSAGE;
}
